// Turns game results into sounds and effects. `act()` in state.tsx calls afterAction() after every
// action; the Shell calls afterRefresh() when the character sheet is polled. Pages with their own
// timing (casino games, fights, bosses, turf battles) play their sounds themselves; those paths are
// listed in SELF_MANAGED so nothing plays twice.
import { play, sequence } from './sound.ts';
import { fx } from './fx.tsx';
import { getSettings } from './settings.ts';

const SELF_MANAGED = [
  '/api/casino/', '/api/poker/', '/api/fight/attack', '/api/hitlist/attack', '/api/rob', '/api/bosses/',
  '/api/territory/attack', '/api/achievements/', '/api/whats-new/', '/api/auth/', '/api/races/bet',
  // v0.7 pages with their own sounds and effects
  '/api/streak/', '/api/missions/', '/api/sidebets/', '/api/territory/siege/', '/api/cityboss/',
  // v0.8
  '/api/auction/', '/api/give/', '/api/trades/', '/api/titles/',
];

const SOUNDS: [string, string][] = [
  ['/api/jobs/do', 'coin'],
  ['/api/properties/collect', 'chaching'],
  ['/api/bank/', 'chaching'],
  ['/api/properties/buy', 'buy'],
  ['/api/store/buy', 'buy'],
  ['/api/territory/units/buy', 'drum1'],
  ['/api/market/buy', 'buy'],
  ['/api/market/bid', 'chips'],
  ['/api/store/sell', 'coins'],
  ['/api/properties/sell', 'coins'],
  ['/api/hospital/heal', 'heal'],
  ['/api/items/use', 'munch'],
  ['/api/wall/like', 'like'],
  ['/api/wall/post', 'pop'],
  ['/api/chat', 'pop'],
  ['/api/mafia/', 'pop'],
  ['/api/gifts/', 'pop'],
  ['/api/skills/allocate', 'chime'],
  ['/api/godfather', 'coins'],
  ['/api/collections/vault', 'chime'],
  ['/api/hitlist/place', 'gunshot'],
  ['/api/heists/plan', 'pop'],
  ['/api/heists/join', 'pop'],
  ['/api/territory/station', 'tab'],
  ['/api/territory/withdraw', 'tab'],
  ['/api/achievements/pin', 'medal'],
];

const money = (n: number) => `$${Math.abs(n).toLocaleString('en-US')}`;

/** Level-ups and new achievements, wherever they came from. Returns true if a fanfare played. */
function milestones(prev: any, next: any, delay = 0): boolean {
  let fanfare = false;
  if (prev && next && next.level > prev.level) {
    setTimeout(() => { play('fanfare'); fx.levelUp(next.level); }, delay);
    fanfare = true;
  }
  const before = prev?.newAchievements?.length ?? 0;
  const after = next?.newAchievements?.length ?? 0;
  if (prev && after > before) setTimeout(() => play('medal'), delay + (fanfare ? 1300 : 150));
  return fanfare;
}

/** Missions that just finished: a rubber stamp per mission, then the bonus chest. */
function missionNews(next: any, delay = 0) {
  const news: any[] = next?.missionNews ?? [];
  news.forEach((n, i) => {
    const at = delay + 700 + i * 1300;
    setTimeout(() => {
      play(n.bonus ? 'chest' : 'stamp');
      if (!n.bonus) setTimeout(() => play('coins'), 250);
      fx.stamp(n.bonus ? 'Bonus chest' : n.weekly ? 'Weekly mission done' : 'Mission complete', `${n.title} · +${money(n.cash)}${n.xp ? ` · +${n.xp} XP` : ''}`, n.bonus ? 'gold' : 'good');
    }, at);
  });
}

/** Paths whose own animation runs first: hold the level-up celebration until it's over. */
const CELEBRATE_AFTER: [string, number][] = [['/api/fight/attack', 1500], ['/api/hitlist/attack', 1500], ['/api/rob', 1300], ['/api/bosses/attack', 1600], ['/api/territory/attack', 1600], ['/api/cityboss/hit', 2400], ['/api/streak/claim', 900]];

/** Floating "+$1,200" / "+15 XP" from where the player clicked. */
export function floatDeltas(prev: any, next: any, delay = 0) {
  if (!prev || !next) return;
  const dc = next.cash - prev.cash;
  const dx = next.xp - prev.xp;
  if (dc) fx.float(`${dc > 0 ? '+' : '−'}${money(dc)}`, dc > 0 ? 'cash' : 'loss', null, delay);
  if (dx > 0) fx.float(`+${dx.toLocaleString('en-US')} XP`, 'xp', null, delay + 180);
}

export function afterAction(path: string, result: any, prev: any, next: any) {
  const self = SELF_MANAGED.some((p) => path.startsWith(p));
  if (!self) {
    const hit = SOUNDS.find(([p]) => path === p || path.startsWith(p));
    let sound = hit?.[1];
    if (path === '/api/jobs/do' && result?.tierMastered) sound = 'chime';
    if (path === '/api/heists/join' && result?.result) sound = result.result.success ? 'chaching' : 'groan';
    if (sound === 'drum1') sequence([[0, 'buy'], [120, 'punch']]);
    else if (sound) play(sound);
    if (path === '/api/properties/collect' && result?.amount > 0) fx.coins(16);
    floatDeltas(prev, next);
  }
  const wait = CELEBRATE_AFTER.find(([p]) => path.startsWith(p))?.[1] ?? 0;
  const leveled = milestones(prev, next, wait);
  missionNews(next, wait + (leveled ? 2600 : 0));
}

export function afterError() {
  play('error');
}

/** Polling found something new (level-up from a heist, a new message, someone did something to you). */
export function afterRefresh(prev: any, next: any, page: string) {
  if (!prev || !next || prev.id !== next.id) return;
  missionNews(next, milestones(prev, next) ? 2600 : 0);
  if (!getSettings().alerts) return;
  if ((next.unreadUpdates ?? 0) > (prev.unreadUpdates ?? 0)) play('alert');
  else if (page !== 'chat' && (next.latestChatId ?? 0) > (prev.latestChatId ?? 0)) play('pop', { volume: 0.6 });
}
